/**
 * THE SUPPORT CASE TABLE (docs/plans/support-decides-stripe-executes-2026-10-18.html,
 * "The decision table"; issue #2272): one row per line of ONE order - each
 * product, the service fee, the delivery fee, and the bank's fee on a dispute -
 * and per row what goes out (to the guest, or to the bank) and who puts it in:
 * the bakery, the courier (a claim Croissant files), Croissant. A row is
 * settled when nothing on it is Unassigned (out - bakery - courier - Croissant).
 *
 * ONE module for the screen and the server: the Support screen fills the table
 * with these presets and shows these locks and this summary; the decide use
 * case recomputes the rows server side and refuses a table this module finds
 * an issue with. Pure, integer minor units, no I/O.
 *
 * The rules it holds:
 *
 *   - the guest column is locked to a preset unless an admin unlocked the
 *     case: "What they did not get" or "Nothing" (a Stripe amount: "Spread over
 *     the whole order"); unlocked, "Everything they paid" or any amount;
 *   - a fraud warning gives back everything left, a dispute is everything left
 *     "taken by the bank" plus the bank's fee - both fixed;
 *   - per row, what goes out never exceeds what was paid less what earlier
 *     decided cases already gave back;
 *   - a product the bakery never supplied puts its baker-price share on the
 *     bakery (it is simply not paid for it);
 *   - on a product, Croissant never carries less than its commission share of
 *     the part the courier does not cover (the courier is liable for the full
 *     guest price of what it damaged or lost);
 *   - nothing is paid by anybody on a row where nothing goes out - except
 *     the rider row, a cost nothing goes out on: the fee of each rider sent
 *     for nothing (the bag was not ready), put on the bakery or Croissant,
 *     never on the guest and never on the courier (founder, 21 October 2026).
 */

export const SUPPORT_REASON_KINDS = [
  'REMOVED',
  'LOST',
  'CANCELLED',
  'COMPLAINT',
  'DISPUTE',
  'FRAUD',
  'STRIPE',
  'OTHER',
  /** A rider left because the bag was not ready: the case opens, always (21 October 2026). */
  'WASTED_RIDER',
  /** Stripe refused the case's refund: the case reopens, its text Stripe's reason. */
  'REFUND_REFUSED',
] as const;
export type SupportReasonKind = (typeof SUPPORT_REASON_KINDS)[number];

/** The reasons the bank brings: each always opens a case of its own (a BANK case). */
export const BANK_REASON_KINDS = [
  'DISPUTE',
  'FRAUD',
] as const satisfies readonly SupportReasonKind[];

/** The reasons a person may open a case with: all but the two only the platform writes. */
export const OPENING_REASON_KINDS = [
  'REMOVED',
  'LOST',
  'CANCELLED',
  'COMPLAINT',
  'DISPUTE',
  'FRAUD',
  'STRIPE',
  'OTHER',
] as const satisfies readonly SupportReasonKind[];

/** The reasons a person may set or change a reason to on an open case. */
export const PERSON_REASON_KINDS = [
  'REMOVED',
  'LOST',
  'CANCELLED',
  'COMPLAINT',
  'STRIPE',
  'OTHER',
] as const satisfies readonly SupportReasonKind[];
export type PersonReasonKind = (typeof PERSON_REASON_KINDS)[number];

/**
 * A refund Ops paid outside Stripe (a bank transfer) after Stripe refused the
 * case's refund (founder, 21 October 2026): its movement is CONFIRMED at once,
 * its reference this prefix and the movement's id - never a Stripe object.
 */
export const PAID_OUTSIDE_STRIPE = 'outside:';

export function paidOutsideStripe(stripeRef: string | null): boolean {
  return stripeRef?.startsWith(PAID_OUTSIDE_STRIPE) === true;
}

/** The longest "which reason" text an OTHER reason carries. */
export const OTHER_REASON_MAX = 80;

export function isBankReason(kind: SupportReasonKind): boolean {
  return kind === 'DISPUTE' || kind === 'FRAUD';
}

export const SUPPORT_ROW_KINDS = [
  'PRODUCT',
  'SERVICE_FEE',
  'SMALL_ORDER_FEE',
  'DELIVERY_FEE',
  'BANK_FEE',
  'RIDER_FEE',
] as const;
export type SupportRowKind = (typeof SUPPORT_ROW_KINDS)[number];

/** Why a product row is marked as not received, for the screen. */
export const SUPPORT_ROW_WHY = ['REMOVED', 'NOT_SUPPLIED', 'NOT_DELIVERED', 'REPORTED'] as const;
export type SupportRowWhy = (typeof SUPPORT_ROW_WHY)[number];

/** One line of the order, as the case's table shows it. Computed by the server. */
export interface SupportTableRow {
  /** Stable within the order: a product component's id (`:removed` for its removed units), or the fee. */
  readonly key: string;
  readonly kind: SupportRowKind;
  readonly label: string;
  /** Units on a product row; null on a fee. */
  readonly quantity: number | null;
  /** What the guest paid for the line; on the bank fee row, the fee; on the rider row, the riders' fees. */
  readonly guestPriceMinor: number;
  /** The bakery's own price of the line, from the placement snapshot; zero on a fee. */
  readonly bakerPriceMinor: number;
  /** What earlier decided cases on this order already gave back on the line. */
  readonly alreadyMinor: number;
  /** The guest received it. */
  readonly received: boolean;
  /** The bakery supplied it (false: removed at the counter, never collected, not made yet). */
  readonly supplied: boolean;
  /** A received line the guest reported (marked on the case). */
  readonly reported: boolean;
  readonly why: SupportRowWhy | null;
}

/**
 * - REFUND: the guest column is chosen with a preset (unlockable by an admin);
 * - FIXED: a Stripe amount the table must give back at least (spread preset);
 * - FRAUD: everything left goes back, fixed by the founder's rule;
 * - DISPUTE: everything left is taken by the bank, plus the bank's fee row.
 */
export const SUPPORT_TABLE_MODES = ['REFUND', 'FIXED', 'FRAUD', 'DISPUTE'] as const;
export type SupportTableMode = (typeof SUPPORT_TABLE_MODES)[number];

export const GUEST_PRESETS = ['MISSING', 'NOTHING', 'EVERYTHING', 'SPREAD', 'CUSTOM'] as const;
export type GuestPreset = (typeof GUEST_PRESETS)[number];

export const PAYER_PRESETS = ['BAKERY', 'COURIER', 'CROISSANT', 'CUSTOM'] as const;
export type PayerPreset = (typeof PAYER_PRESETS)[number];

export interface SupportTableContext {
  readonly mode: SupportTableMode;
  /** An admin unlocked the guest column (recorded on the case). */
  readonly unlocked: boolean;
  /** FIXED: what Stripe gave back (or took), which the table must give back at least. */
  readonly fixedOutMinor: number;
  /** What the courier charged for the order (its quoted fee until billed), rider waits included. */
  readonly courierPriceMinor: number;
}

/** One row of a decided (or proposed) table. */
export interface SupportDecisionRow {
  readonly key: string;
  readonly outMinor: number;
  readonly bakeryMinor: number;
  readonly courierMinor: number;
  readonly croissantMinor: number;
}

export interface SupportTable {
  readonly guestPreset: GuestPreset;
  readonly payerPreset: PayerPreset | null;
  readonly rows: readonly SupportDecisionRow[];
}

/** What is left to give back on a row (nothing on the rider row). */
export function roomOf(row: SupportTableRow): number {
  if (isRiderRow(row)) return 0;
  return Math.max(0, row.guestPriceMinor - row.alreadyMinor);
}

const isProduct = (row: SupportTableRow) => row.kind === 'PRODUCT';
const isBankFee = (row: SupportTableRow) => row.kind === 'BANK_FEE';
/** The rider row: a cost nothing goes out on, which the bakery or Croissant covers. */
export const isRiderRow = (row: Pick<SupportTableRow, 'kind'>) => row.kind === 'RIDER_FEE';
/** A row nothing goes back to the guest on: the bank's fee, the riders' cost. */
const notTheGuests = (row: SupportTableRow) => isBankFee(row) || isRiderRow(row);

/** What the payers of a row must cover: what goes out, or on the rider row its cost. */
function coveredOf(row: SupportTableRow, outMinor: number): number {
  return isRiderRow(row) ? row.guestPriceMinor : outMinor;
}

/** The guest received the row and did not report it. */
export function gotOf(row: SupportTableRow): boolean {
  return row.received && !row.reported;
}

/** The bakery's share of `amountMinor` of a product row, at its own price ratio; zero on a fee. */
export function bakerShareOf(row: SupportTableRow, amountMinor: number): number {
  if (!isProduct(row) || row.guestPriceMinor <= 0 || amountMinor <= 0) return 0;
  if (amountMinor >= row.guestPriceMinor) return row.bakerPriceMinor;
  return Math.round((amountMinor * row.bakerPriceMinor) / row.guestPriceMinor);
}

/**
 * The parts of a row that fill themselves: the bakery's cell on a product it
 * never supplied, and the least Croissant may carry on a product - its
 * commission share of the part the courier does not cover.
 */
export function rowLocks(
  row: SupportTableRow,
  outMinor: number,
  courierMinor: number,
): { readonly bakeryFixed: number | null; readonly croissantMin: number } {
  if (!isProduct(row)) return { bakeryFixed: null, croissantMin: 0 };
  if (!row.supplied) {
    const bakeryFixed = bakerShareOf(row, outMinor);
    return { bakeryFixed, croissantMin: outMinor - bakeryFixed };
  }
  const rest = Math.max(0, outMinor - Math.max(0, courierMinor));
  return { bakeryFixed: null, croissantMin: rest - bakerShareOf(row, rest) };
}

/** The guest presets this case allows, in the order the screen offers them. */
export function guestPresetsFor(context: SupportTableContext): readonly GuestPreset[] {
  switch (context.mode) {
    case 'FRAUD':
    case 'DISPUTE':
      return ['EVERYTHING'];
    case 'FIXED':
      return context.unlocked ? ['SPREAD', 'CUSTOM'] : ['SPREAD'];
    case 'REFUND':
      return context.unlocked
        ? ['MISSING', 'NOTHING', 'EVERYTHING', 'CUSTOM']
        : ['MISSING', 'NOTHING'];
  }
}

/** Whether the guest column is fixed by the case (fraud, dispute): no preset to choose. */
export function guestColumnFixed(context: SupportTableContext): boolean {
  return context.mode === 'FRAUD' || context.mode === 'DISPUTE';
}

/** What goes out on each row under a guest preset (CUSTOM keeps nothing: every row zero). */
export function guestColumnOf(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  preset: GuestPreset,
): ReadonlyMap<string, number> {
  const out = new Map<string, number>();
  if (guestColumnFixed(context)) {
    for (const row of rows)
      out.set(row.key, context.mode === 'FRAUD' && isBankFee(row) ? 0 : roomOf(row));
    return out;
  }
  if (preset === 'SPREAD') return spreadOver(rows, context.fixedOutMinor);
  const nothingArrived = rows.filter(isProduct).every((row) => !gotOf(row));
  for (const row of rows) {
    let value = 0;
    if (isBankFee(row)) value = 0;
    else if (preset === 'EVERYTHING') value = roomOf(row);
    else if (preset === 'MISSING')
      value = isProduct(row) ? (gotOf(row) ? 0 : roomOf(row)) : nothingArrived ? roomOf(row) : 0;
    out.set(row.key, value);
  }
  return out;
}

/** `amountMinor` over the order's rows by what each has left, the remainder on the last row with room. */
function spreadOver(rows: readonly SupportTableRow[], amountMinor: number): Map<string, number> {
  const out = new Map<string, number>();
  const guestRows = rows.filter((row) => !notTheGuests(row));
  const base = guestRows.reduce((sum, row) => sum + roomOf(row), 0);
  const total = Math.min(Math.max(0, amountMinor), base);
  let given = 0;
  let last: string | null = null;
  for (const row of rows) {
    if (notTheGuests(row) || base === 0) {
      out.set(row.key, 0);
      continue;
    }
    const value = Math.floor((total * roomOf(row)) / base);
    out.set(row.key, value);
    given += value;
    if (roomOf(row) > 0) last = row.key;
  }
  if (last !== null) out.set(last, (out.get(last) ?? 0) + total - given);
  return out;
}

/** Who pays a row's `outMinor` under a payer preset (null: only what fills itself). */
export function payersOf(
  row: SupportTableRow,
  outMinor: number,
  preset: PayerPreset | null,
): Omit<SupportDecisionRow, 'key' | 'outMinor'> {
  const none = { bakeryMinor: 0, courierMinor: 0, croissantMinor: 0 };
  // The riders' cost: on the bakery under its preset, on Croissant under every other.
  if (isRiderRow(row))
    return preset === 'BAKERY'
      ? { ...none, bakeryMinor: row.guestPriceMinor }
      : { ...none, croissantMinor: row.guestPriceMinor };
  if (outMinor <= 0) return none;
  if (isProduct(row)) {
    const locks = rowLocks(row, outMinor, 0);
    if (locks.bakeryFixed !== null)
      return {
        ...none,
        bakeryMinor: locks.bakeryFixed,
        croissantMinor: outMinor - locks.bakeryFixed,
      };
    switch (preset) {
      case 'COURIER':
        return { ...none, courierMinor: outMinor };
      case 'BAKERY': {
        const bakeryMinor = bakerShareOf(row, outMinor);
        return { ...none, bakeryMinor, croissantMinor: outMinor - bakeryMinor };
      }
      case 'CROISSANT':
        return { ...none, croissantMinor: outMinor };
      case 'CUSTOM':
      case null:
        return { ...none, croissantMinor: locks.croissantMin };
    }
  }
  switch (preset) {
    case 'COURIER':
      return { ...none, courierMinor: outMinor };
    case 'BAKERY':
    case 'CROISSANT':
      return { ...none, croissantMinor: outMinor };
    case 'CUSTOM':
    case null:
      return none;
  }
}

/** The whole table under a guest preset and a payer preset. */
export function presetTable(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  guestPreset: GuestPreset,
  payerPreset: PayerPreset | null,
): SupportTable {
  const out = guestColumnOf(rows, context, guestPreset);
  return {
    guestPreset: guestColumnFixed(context) ? 'EVERYTHING' : guestPreset,
    payerPreset,
    rows: rows.map((row) => {
      const outMinor = out.get(row.key) ?? 0;
      return { key: row.key, outMinor, ...payersOf(row, outMinor, payerPreset) };
    }),
  };
}

/** The payer preset that fits the case's reasons, when one does (the screen's "Suggested"). */
export function suggestedPayer(reasons: readonly SupportReasonKind[]): PayerPreset | null {
  if (reasons.includes('FRAUD')) return 'CROISSANT';
  if (reasons.includes('LOST')) return 'COURIER';
  if (reasons.includes('CANCELLED') || reasons.includes('STRIPE')) return 'CROISSANT';
  // A rider sent for nothing alone: Croissant (founder, 21 October 2026); the bakery is Ops' to choose.
  if (reasons.length > 0 && reasons.every((kind) => kind === 'WASTED_RIDER')) return 'CROISSANT';
  return null;
}

/** The table a case opens with: its guest column's first preset, and the suggested payer. */
export function initialTable(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  reasons: readonly SupportReasonKind[],
): SupportTable {
  const guest = guestPresetsFor(context)[0] ?? 'NOTHING';
  return presetTable(rows, context, guest, suggestedPayer(reasons));
}

export const SUPPORT_TABLE_ISSUES = [
  /** A row the order does not have, or one of its rows missing. */
  'ROWS_MISMATCH',
  /** A cell that is not a whole, non-negative number of cents. */
  'NOT_WHOLE',
  /** More goes out than was paid less what was already given back. */
  'OVER_ROOM',
  /** The guest column is not one of the presets this case allows without an unlock. */
  'GUEST_LOCKED',
  /** A payer on a row where nothing goes out. */
  'PAYER_ON_EMPTY_ROW',
  /** Out minus the payers is not zero. */
  'UNASSIGNED',
  /** The bakery's cell on a product it never supplied is not its baker-price share. */
  'BAKERY_FIXED',
  /** Croissant carries less than its commission share of what the courier does not cover. */
  'CROISSANT_BELOW_FLOOR',
  /** A rider's cost put on the courier (it is the bakery's or Croissant's). */
  'RIDER_ON_COURIER',
  /** A Stripe amount the table gives back less of. */
  'BELOW_FIXED',
] as const;
export type SupportTableIssueCode = (typeof SUPPORT_TABLE_ISSUES)[number];

export interface SupportTableIssue {
  readonly code: SupportTableIssueCode;
  /** The row it is about; null for the table as a whole. */
  readonly key: string | null;
}

const whole = (value: number) => Number.isSafeInteger(value) && value >= 0;

/** Every reason the table cannot be confirmed; empty when it can. */
export function tableIssues(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  table: SupportTable,
): readonly SupportTableIssue[] {
  const issues: SupportTableIssue[] = [];
  const byKey = new Map(table.rows.map((row) => [row.key, row]));
  if (
    byKey.size !== table.rows.length ||
    table.rows.length !== rows.length ||
    rows.some((row) => !byKey.has(row.key))
  ) {
    return [{ code: 'ROWS_MISMATCH', key: null }];
  }
  for (const row of rows) {
    const cell = byKey.get(row.key) as SupportDecisionRow;
    const values = [cell.outMinor, cell.bakeryMinor, cell.courierMinor, cell.croissantMinor];
    if (!values.every(whole)) {
      issues.push({ code: 'NOT_WHOLE', key: row.key });
      continue;
    }
    if (cell.outMinor > roomOf(row)) issues.push({ code: 'OVER_ROOM', key: row.key });
    const paid = cell.bakeryMinor + cell.courierMinor + cell.croissantMinor;
    if (isRiderRow(row) && cell.courierMinor > 0)
      issues.push({ code: 'RIDER_ON_COURIER', key: row.key });
    if (!isRiderRow(row) && cell.outMinor === 0 && paid > 0)
      issues.push({ code: 'PAYER_ON_EMPTY_ROW', key: row.key });
    else if (paid !== coveredOf(row, cell.outMinor))
      issues.push({ code: 'UNASSIGNED', key: row.key });
    const locks = rowLocks(row, cell.outMinor, cell.courierMinor);
    if (locks.bakeryFixed !== null && cell.bakeryMinor !== locks.bakeryFixed)
      issues.push({ code: 'BAKERY_FIXED', key: row.key });
    if (isProduct(row) && cell.croissantMinor < locks.croissantMin)
      issues.push({ code: 'CROISSANT_BELOW_FLOOR', key: row.key });
  }
  if (!guestColumnAllowed(rows, context, table)) issues.push({ code: 'GUEST_LOCKED', key: null });
  if (context.mode === 'FIXED') {
    const out = table.rows.reduce((sum, row) => sum + row.outMinor, 0);
    const owed = Math.min(context.fixedOutMinor, guestRoomOf(rows));
    if (out < owed) issues.push({ code: 'BELOW_FIXED', key: null });
  }
  return issues;
}

/** Without an unlock the guest column must be exactly one of the allowed presets. */
function guestColumnAllowed(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  table: SupportTable,
): boolean {
  const presets = guestPresetsFor(context);
  if (presets.includes('CUSTOM')) return true;
  const given = new Map(table.rows.map((row) => [row.key, row.outMinor]));
  return presets.some((preset) => {
    const expected = guestColumnOf(rows, context, preset);
    return rows.every((row) => (expected.get(row.key) ?? 0) === (given.get(row.key) ?? 0));
  });
}

/** What the guest can still be given back on the order (the bank's fee aside). */
function guestRoomOf(rows: readonly SupportTableRow[]): number {
  return rows.filter((row) => !notTheGuests(row)).reduce((sum, row) => sum + roomOf(row), 0);
}

export interface SupportTableTotals {
  readonly guestPriceMinor: number;
  readonly bakerPriceMinor: number;
  readonly alreadyMinor: number;
  /** Every row's out, the bank's fee included. */
  readonly outMinor: number;
  /** What goes back to the guest (a dispute: what the bank takes back for them). */
  readonly guestOutMinor: number;
  readonly bakeryMinor: number;
  readonly courierMinor: number;
  readonly croissantMinor: number;
  /** Sum over the rows of |out - payers|: zero when every row is settled. */
  readonly unassignedMinor: number;
}

export function tableTotals(
  rows: readonly SupportTableRow[],
  table: SupportTable,
): SupportTableTotals {
  const byKey = new Map(table.rows.map((row) => [row.key, row]));
  const totals = {
    guestPriceMinor: 0,
    bakerPriceMinor: 0,
    alreadyMinor: 0,
    outMinor: 0,
    guestOutMinor: 0,
    bakeryMinor: 0,
    courierMinor: 0,
    croissantMinor: 0,
    unassignedMinor: 0,
  };
  for (const row of rows) {
    const cell = byKey.get(row.key);
    const out = cell?.outMinor ?? 0;
    const b = cell?.bakeryMinor ?? 0;
    const k = cell?.courierMinor ?? 0;
    const c = cell?.croissantMinor ?? 0;
    if (!notTheGuests(row)) {
      totals.guestPriceMinor += row.guestPriceMinor;
      totals.guestOutMinor += out;
    }
    totals.bakerPriceMinor += row.bakerPriceMinor;
    totals.alreadyMinor += row.alreadyMinor;
    totals.outMinor += out;
    totals.bakeryMinor += b;
    totals.courierMinor += k;
    totals.croissantMinor += c;
    totals.unassignedMinor += Math.abs(coveredOf(row, out) - b - k - c);
  }
  return totals;
}

/** Before, the adjustment this table makes, and after - for one party on this order. */
export interface SupportPartyLine {
  readonly beforeMinor: number;
  readonly adjustmentMinor: number;
  readonly afterMinor: number;
}

/** "What will happen": each party's figure on this order before and after the table. */
export interface SupportCaseSummary {
  readonly guestPays: SupportPartyLine;
  readonly bakeryReceives: SupportPartyLine;
  readonly courierReceives: SupportPartyLine;
  readonly croissantKeeps: SupportPartyLine;
  /** What Croissant keeps before, as its terms: commission + service fee +/- the delivery part. */
  readonly croissantTerms: {
    readonly commissionMinor: number;
    readonly serviceFeeMinor: number;
    /** Guest delivery fee less the courier price: below zero on a discount or a costlier courier. */
    readonly deliveryMinor: number;
  };
  /** The courier's price as the guest's delivery fee and Croissant's part of it, when Croissant pays some. */
  readonly courierSplit: { readonly guestMinor: number; readonly croissantMinor: number } | null;
}

const line = (beforeMinor: number, adjustmentMinor: number): SupportPartyLine => ({
  beforeMinor,
  adjustmentMinor,
  afterMinor: beforeMinor + adjustmentMinor,
});

/**
 * Guest pays = bakery receives + courier receives + Croissant keeps, before
 * and after. The courier starts at what it charged (so Croissant keeps no
 * delivery fee) and a claim lowers it, below zero when the claim is larger.
 * A product the bakery never supplied is never paid, whatever goes back to
 * the guest for it. A rider sent for nothing is already in the courier's
 * price: on Croissant it changes nothing, on the bakery it moves from what
 * the bakery receives to what Croissant keeps.
 */
export function caseSummary(
  rows: readonly SupportTableRow[],
  context: SupportTableContext,
  table: SupportTable,
): SupportCaseSummary {
  const byKey = new Map(table.rows.map((row) => [row.key, row]));
  let guestBefore = 0;
  let bakeryBefore = 0;
  let outAll = 0;
  let guestOut = 0;
  let bakeryAdj = 0;
  let courierAdj = 0;
  let productRoom = 0;
  let serviceRoom = 0;
  let deliveryGuest = 0;
  for (const row of rows) {
    const cell = byKey.get(row.key);
    const out = cell?.outMinor ?? 0;
    const room = roomOf(row);
    outAll += out;
    courierAdj -= cell?.courierMinor ?? 0;
    bakeryAdj -= cell?.bakeryMinor ?? 0;
    // The riders were already paid (the courier's price): the bakery's share comes back to Croissant.
    if (notTheGuests(row)) continue;
    guestBefore += room;
    guestOut += out;
    if (isProduct(row)) {
      const share = bakerShareOf(row, room);
      bakeryBefore += share;
      productRoom += room;
      // Never supplied: never paid, whatever the guest column gives back of it.
      if (!row.supplied) bakeryAdj -= share - (cell?.bakeryMinor ?? 0);
    } else if (row.kind === 'DELIVERY_FEE') deliveryGuest += room;
    else serviceRoom += room;
  }
  const courierBefore = context.courierPriceMinor;
  const croissantBefore = guestBefore - bakeryBefore - courierBefore;
  return {
    guestPays: line(guestBefore, -guestOut),
    bakeryReceives: line(bakeryBefore, bakeryAdj),
    courierReceives: line(courierBefore, courierAdj),
    croissantKeeps: line(croissantBefore, -outAll - bakeryAdj - courierAdj),
    croissantTerms: {
      commissionMinor: productRoom - bakeryBefore,
      serviceFeeMinor: serviceRoom,
      deliveryMinor: deliveryGuest - courierBefore,
    },
    courierSplit:
      courierBefore > deliveryGuest && deliveryGuest > 0
        ? { guestMinor: deliveryGuest, croissantMinor: courierBefore - deliveryGuest }
        : null,
  };
}
